import { ApiResponse } from "src/models";
import { HistoryEntry, HistoryPageArgs } from "src/models/history";
import {
  BulkResult,
  BulkStaffPayload,
  ClassAssignment,
  ListStaffArgs,
  Page,
  ReactivatedStaff,
  StaffClassAssignmentInput,
  StaffDetail,
  StaffSummary,
  UpdateStaffPayload,
  toQueryString,
} from "src/models/staff";
import { apiSlice } from "src/store/apiSlice";

const base = "/api/account/v1/staff";

/** Every staff list, whatever its filters; a staff change can move people in or out of any of them. */
const ALL_LISTS = { type: "Staff" as const, id: "LIST" };
const listTag = (districtId?: string) => ({
  type: "Staff" as const,
  id: `list:${districtId ?? "scope"}`,
});
const personTag = (profileId: string) => ({
  type: "Staff" as const,
  id: profileId,
});
const teachersTag = (classId: string) => ({
  type: "Classes" as const,
  id: `teachers:${classId}`,
});

/** People → Staff (contracts/account-people-api.md §Staff). Responses unwrap BusinessResponse `data`. */
export const staffApiSlice = apiSlice.injectEndpoints({
  endpoints: (builder) => ({
    listStaff: builder.query<Page<StaffSummary>, ListStaffArgs>({
      query: (args) => ({
        url: `${base}${toQueryString(args)}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<Page<StaffSummary>>) => r.data,
      providesTags: (result, _e, args) => [
        ALL_LISTS,
        listTag(args.districtId),
        ...(result?.items ?? []).map((person) => personTag(person.id)),
      ],
    }),
    getStaff: builder.query<StaffDetail, string>({
      query: (profileId) => ({ url: `${base}/${profileId}`, method: "GET" }),
      transformResponse: (r: ApiResponse<StaffDetail>) => r.data,
      providesTags: (_r, _e, profileId) => [personTag(profileId)],
    }),
    updateStaff: builder.mutation<
      StaffDetail,
      { profileId: string; body: UpdateStaffPayload }
    >({
      query: ({ profileId, body }) => ({
        url: `${base}/${profileId}`,
        method: "PATCH",
        body,
      }),
      transformResponse: (r: ApiResponse<StaffDetail>) => r.data,
      invalidatesTags: (_r, _e, { profileId }) => [
        personTag(profileId),
        ALL_LISTS,
      ],
    }),
    deactivateStaff: builder.mutation<StaffDetail, string>({
      query: (profileId) => ({
        url: `${base}/${profileId}/deactivate`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<StaffDetail>) => r.data,
      invalidatesTags: (_r, _e, profileId) => [
        personTag(profileId),
        ALL_LISTS,
        "Classes",
      ],
    }),
    reactivateStaff: builder.mutation<ReactivatedStaff, string>({
      query: (profileId) => ({
        url: `${base}/${profileId}/reactivate`,
        method: "POST",
      }),
      transformResponse: (r: ApiResponse<ReactivatedStaff>) => r.data,
      invalidatesTags: (_r, _e, profileId) => [personTag(profileId), ALL_LISTS],
    }),
    resetStaffPassword: builder.mutation<void, string>({
      query: (profileId) => ({
        url: `${base}/${profileId}/password-reset`,
        method: "POST",
      }),
      transformResponse: () => undefined,
    }),
    getStaffClasses: builder.query<
      ClassAssignment[],
      { profileId: string; includeInactive?: boolean }
    >({
      query: ({ profileId, includeInactive }) => ({
        url: `${base}/${profileId}/classes${toQueryString({ includeInactive })}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<ClassAssignment[]>) => r.data,
      providesTags: (_r, _e, { profileId }) => [personTag(profileId)],
    }),
    setStaffClasses: builder.mutation<
      ClassAssignment[],
      {
        profileId: string;
        assignments: StaffClassAssignmentInput[];
        /** Classes the change removes, so their teacher lists refresh too. Not sent. */
        removedClassIds?: string[];
      }
    >({
      query: ({ profileId, assignments }) => ({
        url: `${base}/${profileId}/classes`,
        method: "PUT",
        body: { assignments },
      }),
      transformResponse: (r: ApiResponse<ClassAssignment[]>) => r.data,
      invalidatesTags: (
        _r,
        _e,
        { profileId, assignments, removedClassIds },
      ) => [
        personTag(profileId),
        ALL_LISTS,
        "Classes",
        ...assignments.map((a) => teachersTag(a.classId)),
        ...(removedClassIds ?? []).map(teachersTag),
      ],
    }),
    /** Who changed what for this person, newest first. Every staff change invalidates it. */
    getStaffHistory: builder.query<
      Page<HistoryEntry>,
      { profileId: string } & HistoryPageArgs
    >({
      query: ({ profileId, limit, offset }) => ({
        url: `${base}/${profileId}/history${toQueryString({ limit, offset })}`,
        method: "GET",
      }),
      transformResponse: (r: ApiResponse<Page<HistoryEntry>>) => r.data,
      providesTags: (_r, _e, { profileId }) => [personTag(profileId)],
    }),
    bulkStaff: builder.mutation<BulkResult, BulkStaffPayload>({
      query: (body) => ({ url: `${base}/bulk`, method: "POST", body }),
      transformResponse: (r: ApiResponse<BulkResult>) => r.data,
      invalidatesTags: (_r, _e, { ids, classIds }) => [
        ALL_LISTS,
        ...ids.map(personTag),
        ...(classIds?.length
          ? ["Classes" as const, ...classIds.map(teachersTag)]
          : []),
      ],
    }),
  }),
});

export const {
  useListStaffQuery,
  useGetStaffQuery,
  useUpdateStaffMutation,
  useDeactivateStaffMutation,
  useReactivateStaffMutation,
  useResetStaffPasswordMutation,
  useGetStaffClassesQuery,
  useSetStaffClassesMutation,
  useGetStaffHistoryQuery,
  useBulkStaffMutation,
} = staffApiSlice;
